import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Printer, Flag, CheckCircle, Factory, XCircle } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

export default function ProductionPlanTimeline() {
  const { id } = useParams();
  const [plan, setPlan] = useState(undefined);

  useEffect(() => {
    api.get(`/production-plans/${id}`).then((r) => setPlan(r.data)).catch(() => setPlan(null));
  }, [id]);

  const buildEvents = (p) => {
    const items = p.items || [];
    const planned = items.reduce((s, it) => s + (it.quantity || 0), 0);
    const events = [
      { type: "start", at: p.created_at, by: p.by_name, label: `Plan raised — ${planned} pcs across ${items.length} item(s)`, sub: p.manager ? `Manager: ${p.manager}` : "" },
    ];
    items.forEach((it) => {
      (it.history || []).forEach((h) => {
        if (h.qty > 0) {
          events.push({ type: "made", at: h.at, by: h.by_name, label: `${h.qty} ${it.unit} made — added to stock`, sub: it.material_name });
        }
        if (h.scrapped > 0) {
          events.push({ type: "scrap", at: h.at, by: h.by_name, label: `${h.scrapped} ${it.unit} scrapped — permanent loss`, sub: it.material_name, reason: h.reason });
        }
      });
    });
    if (p.status === "completed") {
      const last = items.reduce((mx, it) => it.completed_at && it.completed_at > mx ? it.completed_at : mx, "");
      events.push({ type: "done", at: last || p.created_at, by: "", label: "Plan fully completed" });
    }
    return events.sort((a, b) => new Date(a.at) - new Date(b.at));
  };

  const iconFor = (t) => {
    if (t === "start") return <Flag size={16} weight="fill" className="text-zinc-800" />;
    if (t === "made") return <Factory size={16} weight="fill" className="text-[#0891B2]" />;
    if (t === "scrap") return <XCircle size={16} weight="fill" className="text-[#DC2626]" />;
    return <CheckCircle size={16} weight="fill" className="text-[#16A34A]" />;
  };

  const planned = plan ? (plan.items || []).reduce((s, it) => s + (it.quantity || 0), 0) : 0;
  const made = plan ? (plan.items || []).reduce((s, it) => s + (it.completed_qty || 0), 0) : 0;
  const scrapped = plan ? (plan.items || []).reduce((s, it) => s + (it.scrapped_qty || 0), 0) : 0;

  return (
    <>
      <PageHeader
        title={plan ? `${plan.plan_number || "Plan"} Timeline` : "Plan Timeline"}
        subtitle={plan ? `${plan.manager || ""} · ${made} / ${planned} made · ${plan.status}` : plan === null ? "Not found" : "Loading…"}
      >
        {plan && (
          <Button onClick={() => window.print()} data-testid="print-plan-timeline" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider text-xs print:hidden">
            <Printer size={16} weight="bold" className="mr-1" /> Print
          </Button>
        )}
      </PageHeader>
      <div className="p-4 sm:p-8">
        {plan === undefined ? (
          <p className="text-sm text-zinc-400">Loading…</p>
        ) : plan === null ? (
          <p className="text-sm text-zinc-400" data-testid="plan-timeline-not-found">This production plan no longer exists.</p>
        ) : (
          <div className="bg-white border border-zinc-300 max-w-3xl">
            <div className="px-6 py-5 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-sm font-bold bg-zinc-900 text-white px-2 py-1">{plan.plan_number}</span>
                <span className="text-sm font-medium text-zinc-900">{plan.manager}</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase text-white ${plan.status === "completed" ? "bg-[#16A34A]" : plan.status === "cancelled" ? "bg-zinc-500" : "bg-[#0891B2]"}`}>{plan.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Planned</p><p className="font-mono font-bold text-lg">{planned}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Made</p><p className="font-mono font-bold text-lg text-[#0891B2]">{made}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Scrapped</p><p className="font-mono font-bold text-lg text-[#DC2626]">{scrapped}</p></div>
              </div>
              {planned > 0 && (
                <div className="mt-4" data-testid="plan-timeline-bar">
                  <div className="flex h-2.5 w-full overflow-hidden bg-zinc-200">
                    <div className="h-full bg-[#0891B2]" style={{ width: `${(made / planned) * 100}%` }} title={`Made ${made}`} />
                    <div className="h-full bg-[#DC2626]" style={{ width: `${(scrapped / planned) * 100}%` }} title={`Scrap ${scrapped}`} />
                  </div>
                  <div className="flex gap-4 mt-1.5 text-[11px] text-zinc-500">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#0891B2] inline-block" /> Made</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#DC2626] inline-block" /> Scrap</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-zinc-200 inline-block" /> Remaining</span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-6" data-testid="plan-timeline-events">
              <ol className="relative border-l border-zinc-200 ml-2">
                {buildEvents(plan).map((e, i) => (
                  <li key={`${e.type}-${e.at}-${i}`} className="mb-6 ml-6 last:mb-0" data-testid={`plan-timeline-event-${i}`}>
                    <span className="absolute -left-[9px] flex items-center justify-center w-[18px] h-[18px] bg-white">{iconFor(e.type)}</span>
                    <p className={`text-sm font-medium ${e.type === "scrap" ? "text-[#DC2626]" : "text-zinc-900"}`}>{e.label}</p>
                    {e.sub && <p className="text-xs text-zinc-500">{e.sub}</p>}
                    {e.reason && <p className="text-[11px] text-zinc-500 italic mt-0.5">Reason: {e.reason}</p>}
                    <p className="text-[11px] text-zinc-400 mt-0.5">{fmt(e.at)}{e.by ? ` · ${e.by}` : ""}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
